
import React, { useState, useEffect } from 'react'
import { X, Plus, Trash2, Calendar, Clock, ArrowUp, ArrowDown, Users, Package, AlertTriangle } from 'lucide-react'

interface PlanejamentoObraFormProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: any) => Promise<void>
  initialData?: any
  title: string
  contratosAssinados: any[]
  orcamentosFechados: any[]
  funcionarios?: any[]
  insumos?: any[]
}

export const PlanejamentoObraForm: React.FC<PlanejamentoObraFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  title,
  contratosAssinados,
  orcamentosFechados,
  funcionarios = [],
  insumos = []
}) => {
  const [formData, setFormData] = useState({
    contratoId: '',
    orcamentoId: '',
    numeroContrato: '',
    nomeObra: '',
    descricaoProjeto: '',
    responsavelId: '',
    responsavelNome: '',
    status: 'planejamento',
    dataInicioReal: '',
    dataTerminoPrevisto: '',
    dataInicioRealReal: '',
    dataTerminoReal: '',
    valorOrcado: 0,
    valorRealizado: 0,
    custosAdministrativos: 0,
    percentualFaturamento: 0,
    bdi: 0,
    etapas: [],
    etapaAtual: '',
    progressoGeral: 0
  })

  const [etapas, setEtapas] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [showSolicitacaoCompra, setShowSolicitacaoCompra] = useState(false)
  const [insumoSemEstoque, setInsumoSemEstoque] = useState<any>(null)

  useEffect(() => {
    if (initialData) {
      setFormData({
        contratoId: initialData.contratoId || '',
        orcamentoId: initialData.orcamentoId || '',
        numeroContrato: initialData.numeroContrato || '',
        nomeObra: initialData.nomeObra || '',
        descricaoProjeto: initialData.descricaoProjeto || '',
        responsavelId: initialData.responsavelId || '',
        responsavelNome: initialData.responsavelNome || '',
        status: initialData.status || 'planejamento',
        dataInicioReal: initialData.dataInicioReal ? initialData.dataInicioReal.split('T')[0] : '',
        dataTerminoPrevisto: initialData.dataTerminoPrevisto ? initialData.dataTerminoPrevisto.split('T')[0] : '',
        dataInicioRealReal: initialData.dataInicioRealReal ? initialData.dataInicioRealReal.split('T')[0] : '',
        dataTerminoReal: initialData.dataTerminoReal ? initialData.dataTerminoReal.split('T')[0] : '',
        valorOrcado: initialData.valorOrcado || 0,
        valorRealizado: initialData.valorRealizado || 0,
        custosAdministrativos: initialData.custosAdministrativos || 0,
        percentualFaturamento: initialData.percentualFaturamento || 0,
        bdi: initialData.bdi || 0,
        etapas: initialData.etapas || [],
        etapaAtual: initialData.etapaAtual || '',
        progressoGeral: initialData.progressoGeral || 0
      })
      setEtapas(initialData.etapas || [])
    } else {
      setFormData({
        contratoId: '',
        orcamentoId: '',
        numeroContrato: '',
        nomeObra: '',
        descricaoProjeto: '',
        responsavelId: '',
        responsavelNome: '',
        status: 'planejamento',
        dataInicioReal: '',
        dataTerminoPrevisto: '',
        dataInicioRealReal: '',
        dataTerminoReal: '',
        valorOrcado: 0,
        valorRealizado: 0,
        custosAdministrativos: 0,
        percentualFaturamento: 0,
        bdi: 0,
        etapas: [],
        etapaAtual: '',
        progressoGeral: 0
      })
      setEtapas([])
    }
  }, [initialData, isOpen])

  const handleContratoChange = (contratoId: string) => {
    const contrato = contratosAssinados.find(c => c._id === contratoId)
    if (contrato) {
      setFormData(prev => ({
        ...prev,
        contratoId,
        numeroContrato: contrato.numero, // Auto-preenchimento do número do contrato
        nomeObra: contrato.nomeProjeto,
        descricaoProjeto: contrato.descricaoProjeto || '',
        valorOrcado: contrato.valorTotal,
        bdi: contrato.bdi || 0
      }))
      
      // Importar etapas do contrato se existirem
      if (contrato.etapas && contrato.etapas.length > 0) {
        const etapasImportadas = contrato.etapas.map((etapa: any, index: number) => ({
          id: `etapa_${Date.now()}_${index}`,
          numero: `${index + 1}`,
          nome: etapa.nome,
          descricao: etapa.descricao || '',
          ordem: index + 1,
          status: 'previsto',
          dataInicioPrevisto: '',
          dataTerminoPrevisto: '',
          dataInicioReal: '',
          dataTerminoReal: '',
          valorOrcado: etapa.valorTotal || 0,
          valorRealizado: 0,
          percentualConcluido: 0,
          dependencias: [],
          subetapas: [],
          insumos: [],
          maoObra: []
        }))
        setEtapas(etapasImportadas)
      }
    }
  }

  const handleOrcamentoChange = (orcamentoId: string) => {
    const orcamento = orcamentosFechados.find(o => o._id === orcamentoId)
    if (orcamento) {
      setFormData(prev => ({
        ...prev,
        orcamentoId,
        nomeObra: orcamento.nomeProjeto,
        descricaoProjeto: orcamento.descricao || '',
        valorOrcado: orcamento.valorTotal,
        bdi: orcamento.bdi || 0
      }))
      
      // Importar etapas do orçamento se existirem
      if (orcamento.etapas && orcamento.etapas.length > 0) {
        const etapasImportadas = orcamento.etapas.map((etapa: any, index: number) => ({
          id: `etapa_${Date.now()}_${index}`,
          numero: `${index + 1}`,
          nome: etapa.nome,
          descricao: etapa.descricao || '',
          ordem: index + 1,
          status: 'previsto',
          dataInicioPrevisto: '',
          dataTerminoPrevisto: '',
          dataInicioReal: '',
          dataTerminoReal: '',
          valorOrcado: etapa.valorTotal || 0,
          valorRealizado: 0,
          percentualConcluido: 0,
          dependencias: [],
          subetapas: [],
          insumos: [],
          maoObra: []
        }))
        setEtapas(etapasImportadas)
      }
    }
  }

  const handleResponsavelChange = (funcionarioId: string) => {
    const funcionario = funcionarios.find(f => f._id === funcionarioId)
    if (funcionario) {
      setFormData(prev => ({
        ...prev,
        responsavelId: funcionarioId,
        responsavelNome: funcionario.nome
      }))
    }
  }

  // Calcular valor realizado automaticamente
  const calcularValorRealizado = () => {
    let valorTotal = 0
    
    etapas.forEach(etapa => {
      const valorEtapa = (etapa.valorOrcado * etapa.percentualConcluido) / 100
      const valorMaoObra = etapa.maoObra?.reduce((sum: number, mo: any) => sum + (mo.valorTotal || 0), 0) || 0
      const valorMateriais = etapa.insumos?.reduce((sum: number, ins: any) => sum + (ins.valorTotal || 0), 0) || 0
      
      valorTotal += valorEtapa + valorMaoObra + valorMateriais
      
      // Somar subetapas
      etapa.subetapas?.forEach((subetapa: any) => {
        const valorSubetapa = (subetapa.valorOrcado * subetapa.percentualConcluido) / 100
        const valorMaoObraSubetapa = subetapa.maoObra?.reduce((sum: number, mo: any) => sum + (mo.valorTotal || 0), 0) || 0
        const valorMateriaisSubetapa = subetapa.insumos?.reduce((sum: number, ins: any) => sum + (ins.valorTotal || 0), 0) || 0
        
        valorTotal += valorSubetapa + valorMaoObraSubetapa + valorMateriaisSubetapa
      })
    })
    
    return valorTotal
  }

  // Calcular % Faturamento
  const calcularPercentualFaturamento = () => {
    const valorRealizado = calcularValorRealizado()
    const valorMaoObra = etapas.reduce((sum, etapa) => {
      return sum + (etapa.maoObra?.reduce((s: number, mo: any) => s + (mo.valorTotal || 0), 0) || 0)
    }, 0)
    const valorMateriais = etapas.reduce((sum, etapa) => {
      return sum + (etapa.insumos?.reduce((s: number, ins: any) => s + (ins.valorTotal || 0), 0) || 0)
    }, 0)
    
    const medicao = valorRealizado
    const percentual = medicao - valorMaoObra - valorMateriais - formData.custosAdministrativos
    
    return Math.max(0, percentual)
  }

  // Atualizar valores automaticamente
  useEffect(() => {
    const valorRealizado = calcularValorRealizado()
    const percentualFaturamento = calcularPercentualFaturamento()
    
    setFormData(prev => ({
      ...prev,
      valorRealizado,
      percentualFaturamento
    }))
  }, [etapas, formData.custosAdministrativos])

  const addEtapa = () => {
    const newEtapa = {
      id: `etapa_${Date.now()}`,
      numero: `${etapas.length + 1}`,
      nome: '',
      descricao: '',
      ordem: etapas.length + 1,
      status: 'previsto',
      dataInicioPrevisto: '',
      dataTerminoPrevisto: '',
      dataInicioReal: '',
      dataTerminoReal: '',
      valorOrcado: 0,
      valorRealizado: 0,
      percentualConcluido: 0,
      dependencias: [],
      subetapas: [],
      insumos: [],
      maoObra: []
    }
    setEtapas([...etapas, newEtapa])
  }

  const addSubetapa = (etapaIndex: number) => {
    const updatedEtapas = [...etapas]
    const etapa = updatedEtapas[etapaIndex]
    const subetapas = etapa.subetapas || []
    
    const newSubetapa = {
      id: `subetapa_${Date.now()}`,
      numero: `${etapa.numero}.${subetapas.length + 1}`,
      nome: '',
      descricao: '',
      status: 'previsto',
      dataInicioPrevisto: '',
      dataTerminoPrevisto: '',
      dataInicioReal: '',
      dataTerminoReal: '',
      valorOrcado: 0,
      valorRealizado: 0,
      percentualConcluido: 0,
      insumos: [],
      maoObra: []
    }
    
    etapa.subetapas = [...subetapas, newSubetapa]
    setEtapas(updatedEtapas)
  }

  const updateEtapa = (index: number, field: string, value: any) => {
    const updatedEtapas = [...etapas]
    updatedEtapas[index] = { ...updatedEtapas[index], [field]: value }
    setEtapas(updatedEtapas)
  }

  const updateSubetapa = (etapaIndex: number, subetapaIndex: number, field: string, value: any) => {
    const updatedEtapas = [...etapas]
    updatedEtapas[etapaIndex].subetapas[subetapaIndex] = {
      ...updatedEtapas[etapaIndex].subetapas[subetapaIndex],
      [field]: value
    }
    setEtapas(updatedEtapas)
  }

  const removeEtapa = (index: number) => {
    const updatedEtapas = etapas.filter((_, i) => i !== index)
    // Renumerar etapas
    updatedEtapas.forEach((etapa, i) => {
      etapa.numero = `${i + 1}`
      etapa.ordem = i + 1
      // Renumerar subetapas
      etapa.subetapas?.forEach((subetapa: any, j: number) => {
        subetapa.numero = `${i + 1}.${j + 1}`
      })
    })
    setEtapas(updatedEtapas)
  }

  const removeSubetapa = (etapaIndex: number, subetapaIndex: number) => {
    const updatedEtapas = [...etapas]
    updatedEtapas[etapaIndex].subetapas = updatedEtapas[etapaIndex].subetapas.filter((_: any, i: number) => i !== subetapaIndex)
    // Renumerar subetapas
    updatedEtapas[etapaIndex].subetapas?.forEach((subetapa: any, i: number) => {
      subetapa.numero = `${updatedEtapas[etapaIndex].numero}.${i + 1}`
    })
    setEtapas(updatedEtapas)
  }

  const moveEtapa = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= etapas.length) return

    const updatedEtapas = [...etapas]
    const temp = updatedEtapas[index]
    updatedEtapas[index] = updatedEtapas[newIndex]
    updatedEtapas[newIndex] = temp

    // Renumerar etapas
    updatedEtapas.forEach((etapa, i) => {
      etapa.numero = `${i + 1}`
      etapa.ordem = i + 1
      // Renumerar subetapas
      etapa.subetapas?.forEach((subetapa: any, j: number) => {
        subetapa.numero = `${i + 1}.${j + 1}`
      })
    })

    setEtapas(updatedEtapas)
  }

  const addInsumoToEtapa = (etapaIndex: number, subetapaIndex?: number) => {
    const insumoId = (document.getElementById(`insumo-select-${etapaIndex}-${subetapaIndex || 'etapa'}`) as HTMLSelectElement)?.value
    const quantidade = parseFloat((document.getElementById(`insumo-quantidade-${etapaIndex}-${subetapaIndex || 'etapa'}`) as HTMLInputElement)?.value || '0')
    
    if (!insumoId || quantidade <= 0) return

    const insumo = insumos.find(i => i._id === insumoId)
    if (!insumo) return

    // Verificar estoque
    if (insumo.controlaEstoque && insumo.quantidadeEstoque < quantidade) {
      setInsumoSemEstoque({
        insumo,
        quantidadeNecessaria: quantidade,
        quantidadeDisponivel: insumo.quantidadeEstoque
      })
      setShowSolicitacaoCompra(true)
      return
    }

    const novoInsumo = {
      id: `insumo_${Date.now()}`,
      insumoId,
      nome: insumo.nome,
      quantidade,
      unidade: insumo.unidadeMedida,
      precoUnitario: insumo.precoUnitario,
      valorTotal: quantidade * insumo.precoUnitario
    }

    const updatedEtapas = [...etapas]
    if (subetapaIndex !== undefined) {
      updatedEtapas[etapaIndex].subetapas[subetapaIndex].insumos = [
        ...(updatedEtapas[etapaIndex].subetapas[subetapaIndex].insumos || []),
        novoInsumo
      ]
    } else {
      updatedEtapas[etapaIndex].insumos = [
        ...(updatedEtapas[etapaIndex].insumos || []),
        novoInsumo
      ]
    }
    setEtapas(updatedEtapas)

    // Limpar campos
    ;(document.getElementById(`insumo-select-${etapaIndex}-${subetapaIndex || 'etapa'}`) as HTMLSelectElement).value = ''
    ;(document.getElementById(`insumo-quantidade-${etapaIndex}-${subetapaIndex || 'etapa'}`) as HTMLInputElement).value = ''
  }

  const addMaoObraToEtapa = (etapaIndex: number, subetapaIndex?: number) => {
    const funcionarioId = (document.getElementById(`funcionario-select-${etapaIndex}-${subetapaIndex || 'etapa'}`) as HTMLSelectElement)?.value
    const horas = parseFloat((document.getElementById(`funcionario-horas-${etapaIndex}-${subetapaIndex || 'etapa'}`) as HTMLInputElement)?.value || '0')
    
    if (!funcionarioId || horas <= 0) return

    const funcionario = funcionarios.find(f => f._id === funcionarioId)
    if (!funcionario) return

    const valorHora = funcionario.unidadeRemuneracao === 'hora' ? funcionario.valorRemuneracao : funcionario.valorRemuneracao / (8 * 22)
    
    const novaMaoObra = {
      id: `maoobra_${Date.now()}`,
      funcionarioId,
      nome: funcionario.nome,
      cargo: funcionario.cargo,
      horas,
      valorHora,
      valorTotal: horas * valorHora
    }

    const updatedEtapas = [...etapas]
    if (subetapaIndex !== undefined) {
      updatedEtapas[etapaIndex].subetapas[subetapaIndex].maoObra = [
        ...(updatedEtapas[etapaIndex].subetapas[subetapaIndex].maoObra || []),
        novaMaoObra
      ]
    } else {
      updatedEtapas[etapaIndex].maoObra = [
        ...(updatedEtapas[etapaIndex].maoObra || []),
        novaMaoObra
      ]
    }
    setEtapas(updatedEtapas)

    // Limpar campos
    ;(document.getElementById(`funcionario-select-${etapaIndex}-${subetapaIndex || 'etapa'}`) as HTMLSelectElement).value = ''
    ;(document.getElementById(`funcionario-horas-${etapaIndex}-${subetapaIndex || 'etapa'}`) as HTMLInputElement).value = ''
  }

  const handleSolicitarCompra = () => {
    // Aqui implementaria a lógica para criar uma solicitação de compra
    console.log('Solicitando compra para:', insumoSemEstoque)
    // Por enquanto, apenas fecha o modal
    setShowSolicitacaoCompra(false)
    setInsumoSemEstoque(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const submitData = {
        ...formData,
        etapas,
        dataInicioReal: formData.dataInicioReal ? new Date(formData.dataInicioReal).toISOString() : '',
        dataTerminoPrevisto: formData.dataTerminoPrevisto ? new Date(formData.dataTerminoPrevisto).toISOString() : '',
        dataInicioRealReal: formData.dataInicioRealReal ? new Date(formData.dataInicioRealReal).toISOString() : '',
        dataTerminoReal: formData.dataTerminoReal ? new Date(formData.dataTerminoReal).toISOString() : ''
      }

      await onSubmit(submitData)
      onClose()
    } catch (error) {
      console.error('Erro ao salvar:', error)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-7xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6 z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Contrato */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contrato *
              </label>
              <select
                value={formData.contratoId}
                onChange={(e) => handleContratoChange(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Selecione um contrato</option>
                {contratosAssinados.map(contrato => (
                  <option key={contrato._id} value={contrato._id}>
                    {contrato.numero} - {contrato.nomeProjeto}
                  </option>
                ))}
              </select>
            </div>

            {/* Orçamento Fechado */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Orçamento Fechado (Opcional)
              </label>
              <select
                value={formData.orcamentoId}
                onChange={(e) => handleOrcamentoChange(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Selecione um orçamento fechado</option>
                {orcamentosFechados.map(orcamento => (
                  <option key={orcamento._id} value={orcamento._id}>
                    {orcamento.nomeProjeto} - {orcamento.nomeCliente}
                  </option>
                ))}
              </select>
            </div>

            {/* Número do Contrato (Auto-preenchido) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Número do Contrato
              </label>
              <input
                type="text"
                value={formData.numeroContrato}
                readOnly
                className="w-full p-3 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                placeholder="Será preenchido automaticamente"
              />
            </div>

            {/* Responsável */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Responsável pela Obra *
              </label>
              <select
                value={formData.responsavelId}
                onChange={(e) => handleResponsavelChange(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Selecione um responsável</option>
                {funcionarios.map(funcionario => (
                  <option key={funcionario._id} value={funcionario._id}>
                    {funcionario.nome} - {funcionario.cargo}
                  </option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status *
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="planejamento">Planejamento</option>
                <option value="em_andamento">Em Andamento</option>
                <option value="pausada">Pausada</option>
                <option value="concluida">Concluída</option>
                <option value="cancelada">Cancelada</option>
              </select>
            </div>

            {/* Nome da Obra */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome da Obra *
              </label>
              <input
                type="text"
                value={formData.nomeObra}
                onChange={(e) => setFormData(prev => ({ ...prev, nomeObra: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          {/* Datas */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data de Início Previsto
              </label>
              <input
                type="date"
                value={formData.dataInicioReal}
                onChange={(e) => setFormData(prev => ({ ...prev, dataInicioReal: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data de Término Previsto
              </label>
              <input
                type="date"
                value={formData.dataTerminoPrevisto}
                onChange={(e) => setFormData(prev => ({ ...prev, dataTerminoPrevisto: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data de Início Real
              </label>
              <input
                type="date"
                value={formData.dataInicioRealReal}
                onChange={(e) => setFormData(prev => ({ ...prev, dataInicioRealReal: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data de Término Real
              </label>
              <input
                type="date"
                value={formData.dataTerminoReal}
                onChange={(e) => setFormData(prev => ({ ...prev, dataTerminoReal: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Descrição do Projeto */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descrição do Projeto
            </label>
            <textarea
              value={formData.descricaoProjeto}
              onChange={(e) => setFormData(prev => ({ ...prev, descricaoProjeto: e.target.value }))}
              rows={3}
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Valores Financeiros */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Valor Orçado (R$)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.valorOrcado}
                onChange={(e) => setFormData(prev => ({ ...prev, valorOrcado: parseFloat(e.target.value) || 0 }))}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Valor Realizado (R$) - Automático
              </label>
              <input
                type="number"
                value={formData.valorRealizado}
                readOnly
                className="w-full p-3 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Custos Administrativos (R$)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.custosAdministrativos}
                onChange={(e) => setFormData(prev => ({ ...prev, custosAdministrativos: parseFloat(e.target.value) || 0 }))}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Vem do Financeiro"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                % Faturamento - Automático
              </label>
              <input
                type="number"
                value={formData.percentualFaturamento}
                readOnly
                className="w-full p-3 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Progresso Geral (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={formData.progressoGeral}
                onChange={(e) => setFormData(prev => ({ ...prev, progressoGeral: parseFloat(e.target.value) || 0 }))}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Etapas */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Etapas e Subetapas</h3>
              <button
                type="button"
                onClick={addEtapa}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2"
              >
                <Plus size={16} />
                Adicionar Etapa
              </button>
            </div>

            {etapas.map((etapa, etapaIndex) => (
              <div key={etapa.id} className="border border-gray-200 rounded-lg p-4 mb-4">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-medium">Etapa {etapa.numero} - {etapa.nome}</h4>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => moveEtapa(etapaIndex, 'up')}
                      disabled={etapaIndex === 0}
                      className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                    >
                      <ArrowUp size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveEtapa(etapaIndex, 'down')}
                      disabled={etapaIndex === etapas.length - 1}
                      className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                    >
                      <ArrowDown size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => addSubetapa(etapaIndex)}
                      className="bg-green-600 text-white px-2 py-1 rounded text-sm hover:bg-green-700"
                    >
                      + Subetapa
                    </button>
                    <button
                      type="button"
                      onClick={() => removeEtapa(etapaIndex)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {/* Campos da Etapa */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome da Etapa
                    </label>
                    <input
                      type="text"
                      value={etapa.nome}
                      onChange={(e) => updateEtapa(etapaIndex, 'nome', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={etapa.status}
                      onChange={(e) => updateEtapa(etapaIndex, 'status', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="previsto">Previsto</option>
                      <option value="em_andamento">Em Andamento</option>
                      <option value="realizado">Realizado</option>
                      <option value="atrasado">Atrasado</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      % Concluído
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={etapa.percentualConcluido}
                      onChange={(e) => updateEtapa(etapaIndex, 'percentualConcluido', parseFloat(e.target.value) || 0)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Dependências */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dependências (Etapas que devem ser concluídas antes)
                  </label>
                  <select
                    multiple
                    value={etapa.dependencias || []}
                    onChange={(e) => {
                      const values = Array.from(e.target.selectedOptions, option => option.value)
                      updateEtapa(etapaIndex, 'dependencias', values)
                    }}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {etapas.filter((_, i) => i !== etapaIndex).map((etapaOption, i) => (
                      <option key={etapaOption.id} value={etapaOption.id}>
                        Etapa {etapaOption.numero} - {etapaOption.nome}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Insumos da Etapa */}
                <div className="mb-4">
                  <h5 className="font-medium mb-2 flex items-center gap-2">
                    <Package size={16} />
                    Insumos da Etapa
                  </h5>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                    <select
                      id={`insumo-select-${etapaIndex}-etapa`}
                      className="p-2 border border-gray-300 rounded-md"
                    >
                      <option value="">Selecione um insumo</option>
                      {insumos.map(insumo => (
                        <option key={insumo._id} value={insumo._id}>
                          {insumo.nome} - {insumo.unidadeMedida}
                        </option>
                      ))}
                    </select>
                    <input
                      id={`insumo-quantidade-${etapaIndex}-etapa`}
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Quantidade"
                      className="p-2 border border-gray-300 rounded-md"
                    />
                    <button
                      type="button"
                      onClick={() => addInsumoToEtapa(etapaIndex)}
                      className="bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700"
                    >
                      Adicionar
                    </button>
                  </div>
                  {etapa.insumos?.map((insumo: any) => (
                    <div key={insumo.id} className="flex justify-between items-center bg-gray-50 p-2 rounded mb-1">
                      <span>{insumo.nome} - {insumo.quantidade} {insumo.unidade}</span>
                      <span className="font-medium">R$ {insumo.valorTotal.toFixed(2)}</span>
                    </div>
                  ))}
                </div>

                {/* Mão de Obra da Etapa */}
                <div className="mb-4">
                  <h5 className="font-medium mb-2 flex items-center gap-2">
                    <Users size={16} />
                    Mão de Obra da Etapa
                  </h5>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                    <select
                      id={`funcionario-select-${etapaIndex}-etapa`}
                      className="p-2 border border-gray-300 rounded-md"
                    >
                      <option value="">Selecione um funcionário</option>
                      {funcionarios.map(funcionario => (
                        <option key={funcionario._id} value={funcionario._id}>
                          {funcionario.nome} - {funcionario.cargo}
                        </option>
                      ))}
                    </select>
                    <input
                      id={`funcionario-horas-${etapaIndex}-etapa`}
                      type="number"
                      min="0"
                      step="0.5"
                      placeholder="Horas"
                      className="p-2 border border-gray-300 rounded-md"
                    />
                    <button
                      type="button"
                      onClick={() => addMaoObraToEtapa(etapaIndex)}
                      className="bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700"
                    >
                      Adicionar
                    </button>
                  </div>
                  {etapa.maoObra?.map((mo: any) => (
                    <div key={mo.id} className="flex justify-between items-center bg-gray-50 p-2 rounded mb-1">
                      <span>{mo.nome} - {mo.horas}h</span>
                      <span className="font-medium">R$ {mo.valorTotal.toFixed(2)}</span>
                    </div>
                  ))}
                </div>

                {/* Subetapas */}
                {etapa.subetapas?.map((subetapa: any, subetapaIndex: number) => (
                  <div key={subetapa.id} className="ml-6 border-l-2 border-blue-200 pl-4 mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <h5 className="font-medium">Subetapa {subetapa.numero} - {subetapa.nome}</h5>
                      <button
                        type="button"
                        onClick={() => removeSubetapa(etapaIndex, subetapaIndex)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                      <input
                        type="text"
                        value={subetapa.nome}
                        onChange={(e) => updateSubetapa(etapaIndex, subetapaIndex, 'nome', e.target.value)}
                        placeholder="Nome da subetapa"
                        className="p-2 border border-gray-300 rounded-md"
                      />
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={subetapa.percentualConcluido}
                        onChange={(e) => updateSubetapa(etapaIndex, subetapaIndex, 'percentualConcluido', parseFloat(e.target.value) || 0)}
                        placeholder="% Concluído"
                        className="p-2 border border-gray-300 rounded-md"
                      />
                    </div>

                    {/* Insumos da Subetapa */}
                    <div className="mb-2">
                      <h6 className="text-sm font-medium mb-1">Insumos da Subetapa</h6>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-1">
                        <select
                          id={`insumo-select-${etapaIndex}-${subetapaIndex}`}
                          className="p-1 border border-gray-300 rounded text-sm"
                        >
                          <option value="">Selecione um insumo</option>
                          {insumos.map(insumo => (
                            <option key={insumo._id} value={insumo._id}>
                              {insumo.nome}
                            </option>
                          ))}
                        </select>
                        <input
                          id={`insumo-quantidade-${etapaIndex}-${subetapaIndex}`}
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Qtd"
                          className="p-1 border border-gray-300 rounded text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => addInsumoToEtapa(etapaIndex, subetapaIndex)}
                          className="bg-blue-600 text-white px-2 py-1 rounded text-sm hover:bg-blue-700"
                        >
                          +
                        </button>
                      </div>
                      {subetapa.insumos?.map((insumo: any) => (
                        <div key={insumo.id} className="text-sm bg-gray-50 p-1 rounded mb-1">
                          {insumo.nome} - {insumo.quantidade} {insumo.unidade} - R$ {insumo.valorTotal.toFixed(2)}
                        </div>
                      ))}
                    </div>

                    {/* Mão de Obra da Subetapa */}
                    <div className="mb-2">
                      <h6 className="text-sm font-medium mb-1">Mão de Obra da Subetapa</h6>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-1">
                        <select
                          id={`funcionario-select-${etapaIndex}-${subetapaIndex}`}
                          className="p-1 border border-gray-300 rounded text-sm"
                        >
                          <option value="">Selecione funcionário</option>
                          {funcionarios.map(funcionario => (
                            <option key={funcionario._id} value={funcionario._id}>
                              {funcionario.nome}
                            </option>
                          ))}
                        </select>
                        <input
                          id={`funcionario-horas-${etapaIndex}-${subetapaIndex}`}
                          type="number"
                          min="0"
                          step="0.5"
                          placeholder="Horas"
                          className="p-1 border border-gray-300 rounded text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => addMaoObraToEtapa(etapaIndex, subetapaIndex)}
                          className="bg-green-600 text-white px-2 py-1 rounded text-sm hover:bg-green-700"
                        >
                          +
                        </button>
                      </div>
                      {subetapa.maoObra?.map((mo: any) => (
                        <div key={mo.id} className="text-sm bg-gray-50 p-1 rounded mb-1">
                          {mo.nome} - {mo.horas}h - R$ {mo.valorTotal.toFixed(2)}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Botões */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>

      {/* Modal de Solicitação de Compra */}
      {showSolicitacaoCompra && insumoSemEstoque && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="text-yellow-500" size={24} />
              <h3 className="text-lg font-semibold">Estoque Insuficiente</h3>
            </div>
            <div className="mb-4">
              <p className="text-gray-700 mb-2">
                <strong>{insumoSemEstoque.insumo.nome}</strong>
              </p>
              <p className="text-sm text-gray-600">
                Quantidade necessária: <strong>{insumoSemEstoque.quantidadeNecessaria}</strong>
              </p>
              <p className="text-sm text-gray-600">
                Quantidade disponível: <strong>{insumoSemEstoque.quantidadeDisponivel}</strong>
              </p>
              <p className="text-sm text-red-600 mt-2">
                Faltam: <strong>{insumoSemEstoque.quantidadeNecessaria - insumoSemEstoque.quantidadeDisponivel}</strong> unidades
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowSolicitacaoCompra(false)
                  setInsumoSemEstoque(null)
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSolicitarCompra}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Solicitar Compra
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PlanejamentoObraForm
